import { XRAY_CONFIGURATION, XRAY_CONFIGURATION_TEST } from '@mm/api-routes';
import AuthenticatedController from './AuthenticatedController';
import { XrayConfigurationPayload } from '../interfaces/xrayConfiguration';
import { camelize, snake } from '../helpers/converterString';
import { camelCase, snakeCase } from 'lodash';
import {
  isDefaultFacebookConversionApiEventId,
  isDefaultFacebookConversionApiEventIdSnakeCase,
} from '@mm/state/helpers/capi-helpers';

export default class XRayConfigurationController extends AuthenticatedController {
  static mapXrayEventsConfigCase(res: XrayConfigurationPayload) {
    const mappedConfig = camelize(res);

    const configKeys = Object.keys(res?.configurations || {});
    const eventsCountsKeys = Object.keys(res?.eventsCounts || {});
    const mapKeys = (keys, source) =>
      keys.reduce((acc, k) => {
        const key = isDefaultFacebookConversionApiEventIdSnakeCase(k)
          ? camelCase(k)
          : k;
        acc[key] = source[k];
        return acc;
      }, {});

    // preserve user case for custom events
    if (configKeys.length) {
      mappedConfig.configurations = mapKeys(configKeys, res.configurations);
    }
    if (eventsCountsKeys.length) {
      mappedConfig.eventsCounts = mapKeys(eventsCountsKeys, res.eventsCounts);
    }

    return mappedConfig;
  }

  static async getXrayEventsConfiguration({
    botId,
    promoterId,
  }: {
    botId: number;
    promoterId?: number;
  }): Promise<XrayConfigurationPayload> {
    return (
      super
        .get(
          XRAY_CONFIGURATION(botId),
          promoterId
            ? {
                promoterId,
              }
            : undefined,
          undefined,
          undefined,
          true,
        )
        // eslint-disable-next-line promise/prefer-await-to-then
        .then(XRayConfigurationController.mapXrayEventsConfigCase)
    );
  }

  static async updateXrayEventsConfiguration(
    botId: number,
    formData: Pick<XrayConfigurationPayload, 'configurations'> &
      Partial<Omit<XrayConfigurationPayload, 'botId' | 'configurations'>>,
  ): Promise<XrayConfigurationPayload> {
    const snakeFormData = snake(formData);

    const configKeys = Object.keys(formData.configurations || {});

    // preserve user case for custom events
    if (configKeys.length) {
      snakeFormData.configurations = configKeys.reduce(
        (a, k) => ({
          ...a,
          [isDefaultFacebookConversionApiEventId(k) ? snakeCase(k) : k]:
            formData.configurations[k],
        }),
        {},
      );
    }

    return (
      super
        .put(
          XRAY_CONFIGURATION(botId),
          snakeFormData,
          undefined,
          undefined,
          true,
          null,
          true,
        )
        // eslint-disable-next-line promise/prefer-await-to-then
        .then(XRayConfigurationController.mapXrayEventsConfigCase)
    );
  }

  static async testXrayEventsConfiguration(botId: number): Promise<{
    response: {
      eventsReceived?: number;
      fbtraceId?: string;
      messages?: string[];
      error: {
        code: number;
        message?: string;
        type: string;
        fbtraceId: string;
      };
    };
  }> {
    return super.post(XRAY_CONFIGURATION_TEST(botId));
  }
}
